type UnlayerPollValues = {
  pollId?: string;
  question?: string;
  pollOptions?: string;
  alignment?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  questionColor?: string;
  buttonPadding?: string;
  buttonBorderRadius?: string;
};

type UnlayerPollOptionValue = {
  id: string;
  text: string;
};

type UnlayerPollWidget = "alignment" | "color_picker" | "padding" | "poll_choices" | "px" | "text";

type UnlayerPollOption = {
  label: string;
  defaultValue: string;
  widget: UnlayerPollWidget;
};

type UnlayerPollOptionGroup = {
  title: string;
  position: number;
  collapsed: boolean;
  options: Record<string, UnlayerPollOption>;
};

type UnlayerPollToolDefinition = {
  name: string;
  label: string;
  icon: string;
  pollResponseUrlPrefix: string;
  supportedDisplayModes: ["email"];
  options: Record<string, UnlayerPollOptionGroup>;
  values: UnlayerPollValues;
};

type UnlayerCustomToolHost = {
  createViewer?: (viewer: { render: (values: UnlayerPollValues) => string }) => unknown;
  registerPropertyEditor?: (propertyEditor: unknown) => void;
  registerTool: (tool: unknown) => void;
  React?: typeof import("react");
};

type PollCustomToolConfig = {
  pollResponseUrlPrefix: string;
};

const DEFAULT_POLL_OPTIONS = '[{"id":"","text":""}]';
const POLL_CUSTOM_TOOL_VERSION = "2";

export function makePollCustomToolUrl(origin: string, accountId: string): string {
  const url = new URL(
    `/api/v1/email-content/poll-custom-tool/${encodeURIComponent(accountId)}.js`,
    origin,
  );
  url.searchParams.set("v", POLL_CUSTOM_TOOL_VERSION);
  return url.toString();
}

export function makePollCustomToolSource(
  origin: string,
  accountId: string,
  config: PollCustomToolConfig,
): string {
  return new URL(origin).protocol === "https:"
    ? makePollCustomToolUrl(origin, accountId)
    : makePollCustomTool(config);
}

export function makePollCustomTool(config: PollCustomToolConfig): string {
  return makeUnlayerCustomToolScript(registerPollCustomTool, {
    name: "poll",
    label: "Poll",
    icon: "fa-bar-chart",
    pollResponseUrlPrefix: config.pollResponseUrlPrefix,
    supportedDisplayModes: ["email"],
    options: {
      content: {
        title: "Poll",
        position: 1,
        collapsed: false,
        options: {
          question: {
            label: "Question",
            defaultValue: "Enter your poll question",
            widget: "text",
          },
          pollOptions: {
            label: "Choices",
            defaultValue: DEFAULT_POLL_OPTIONS,
            widget: "poll_choices",
          },
        },
      },
      style: {
        title: "Button Colors",
        position: 2,
        collapsed: false,
        options: {
          buttonColor: {
            label: "Button color",
            defaultValue: "#185abc",
            widget: "color_picker",
          },
          buttonTextColor: {
            label: "Button text color",
            defaultValue: "#ffffff",
            widget: "color_picker",
          },
        },
      },
      layout: {
        title: "Layout",
        position: 3,
        collapsed: false,
        options: {
          alignment: {
            label: "Alignment",
            defaultValue: "center",
            widget: "alignment",
          },
          questionColor: {
            label: "Question color",
            defaultValue: "#111827",
            widget: "color_picker",
          },
          buttonBorderRadius: {
            label: "Button radius",
            defaultValue: "6px",
            widget: "px",
          },
        },
      },
      buttonPaddingSettings: {
        title: "Button Padding",
        position: 4,
        collapsed: false,
        options: {
          buttonPadding: {
            label: "Button Padding",
            defaultValue: "12px",
            widget: "padding",
          },
        },
      },
    },
    values: {
      pollId: "",
      question: "Enter your poll question",
      pollOptions: DEFAULT_POLL_OPTIONS,
      alignment: "center",
      buttonColor: "#185abc",
      buttonTextColor: "#ffffff",
      questionColor: "#111827",
      buttonPadding: "12px",
      buttonBorderRadius: "6px",
    },
  });
}

function makeUnlayerCustomToolScript(
  registerTool: (definition: UnlayerPollToolDefinition) => void,
  definition: UnlayerPollToolDefinition,
): string {
  return `(() => {
    const __name = (target, value) => Object.defineProperty(target, "name", { value, configurable: true });
    (${registerTool.toString()})(${JSON.stringify(definition)});
  })();`;
}

function registerPollCustomTool(definition: UnlayerPollToolDefinition): void {
  const unlayer = (globalThis as unknown as { unlayer?: UnlayerCustomToolHost }).unlayer;
  if (!unlayer) return;
  const unlayerHost = unlayer;
  const React = unlayerHost.React;
  const { pollResponseUrlPrefix, ...toolDefinition } = definition;

  function escapeHtml(value: unknown): string {
    if (typeof value !== "string") return "";
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function makePollOption(text: string): UnlayerPollOptionValue {
    return {
      id: crypto.randomUUID(),
      text,
    };
  }

  function makeDefaultPollOptions(): UnlayerPollOptionValue[] {
    return [{ id: "", text: "" }];
  }

  function isPollOption(value: unknown): value is UnlayerPollOptionValue {
    return (
      !!value &&
      typeof value === "object" &&
      typeof (value as { id?: unknown }).id === "string" &&
      typeof (value as { text?: unknown }).text === "string"
    );
  }

  function normalizePollOptions(values: UnlayerPollValues): UnlayerPollOptionValue[] {
    if (typeof values.pollOptions !== "string") return makeDefaultPollOptions();

    try {
      const options = JSON.parse(values.pollOptions) as unknown;
      if (!Array.isArray(options) || options.length === 0) {
        return makeDefaultPollOptions();
      }

      const validOptions = options.filter(isPollOption);
      return validOptions.length > 0 ? validOptions : makeDefaultPollOptions();
    } catch {
      return makeDefaultPollOptions();
    }
  }

  function normalizeAlignment(value: unknown): "center" | "left" | "right" {
    return value === "left" || value === "right" ? value : "center";
  }

  function normalizeColor(value: unknown, fallback: string): string {
    if (typeof value !== "string") return fallback;
    return /^(?:#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})|transparent)$/i.test(value)
      ? value
      : fallback;
  }

  function normalizeCssLengths(value: unknown, fallback: string): string {
    if (typeof value !== "string") return fallback;
    const lengths = value.trim().split(/\s+/);
    if (
      lengths.length < 1 ||
      lengths.length > 4 ||
      !lengths.every((length) => /^(?:0|\d+(?:\.\d+)?px)$/.test(length))
    ) {
      return fallback;
    }
    return lengths.join(" ");
  }

  function registerPollChoicesEditor(): void {
    if (!React || typeof unlayerHost.registerPropertyEditor !== "function") return;
    const ReactHost = React;

    function PollChoicesEditor(props: {
      value?: string;
      onChange?: (value: string) => void;
      updateValue?: (value: string) => void;
    }): import("react").ReactNode {
      const values = normalizePollOptions({ pollOptions: props.value });
      const updateValue = (nextValue: UnlayerPollOptionValue[]): void => {
        const normalized = nextValue.length > 0 ? nextValue : [makePollOption("")];
        const serialized = JSON.stringify(normalized);
        props.updateValue?.(serialized);
        props.onChange?.(serialized);
      };

      return ReactHost.createElement(
        "div",
        { style: { display: "flex", flexDirection: "column", gap: 8 } },
        ...values.map((value, index) =>
          ReactHost.createElement(
            "div",
            {
              key: `choice-${index}`,
              style: { display: "flex", gap: 6, alignItems: "center" },
            },
            ReactHost.createElement("input", {
              "aria-label": `Choice ${index + 1}`,
              onChange: (event: { target: { value: string } }) => {
                const nextValue = values.slice();
                nextValue[index] = value.id
                  ? { ...value, text: event.target.value }
                  : makePollOption(event.target.value);
                updateValue(nextValue);
              },
              placeholder: `Choice ${index + 1}`,
              style: {
                border: "1px solid #d1d5db",
                borderRadius: 4,
                boxSizing: "border-box",
                flex: 1,
                fontSize: 13,
                minWidth: 0,
                padding: "8px 10px",
              },
              type: "text",
              value: value.text,
            }),
            ReactHost.createElement(
              "button",
              {
                "aria-label": `Remove choice ${index + 1}`,
                disabled: values.length === 1,
                onClick: () =>
                  updateValue(values.filter((_, removeIndex) => removeIndex !== index)),
                style: {
                  background: "transparent",
                  border: "1px solid #d1d5db",
                  borderRadius: 4,
                  color: values.length === 1 ? "#9ca3af" : "#374151",
                  cursor: values.length === 1 ? "not-allowed" : "pointer",
                  fontSize: 12,
                  padding: "8px 10px",
                },
                type: "button",
              },
              "Remove",
            ),
          ),
        ),
        ReactHost.createElement(
          "button",
          {
            onClick: () => updateValue([...values, makePollOption("")]),
            style: {
              background: "#ffffff",
              border: "1px solid #185abc",
              borderRadius: 4,
              color: "#185abc",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 700,
              padding: "8px 10px",
            },
            type: "button",
          },
          "Add another option",
        ),
      );
    }

    unlayerHost.registerPropertyEditor({
      name: "poll_choices",
      layout: "bottom",
      Widget: PollChoicesEditor,
    });
  }

  function renderButton(option: UnlayerPollOptionValue, values: UnlayerPollValues): string {
    if (!option.id.trim() || !option.text.trim()) return "";

    const style = [
      "display:inline-block",
      "box-sizing:border-box",
      "min-width:120px",
      "margin:4px",
      `padding:${normalizeCssLengths(values.buttonPadding, "12px")}`,
      `border-radius:${normalizeCssLengths(values.buttonBorderRadius, "6px")}`,
      `background:${normalizeColor(values.buttonColor, "#185abc")}`,
      `color:${normalizeColor(values.buttonTextColor, "#ffffff")}`,
      "font-family:Arial, sans-serif",
      "font-size:14px",
      "font-weight:700",
      "line-height:18px",
      "text-align:center",
      "text-decoration:none",
    ].join(";");

    const href = `${pollResponseUrlPrefix}${option.id}`;

    return `<a href="${escapeHtml(href)}" style="${style}">${escapeHtml(option.text)}</a>`;
  }

  function expandCssLengths(value: string): [number, number, number, number] {
    const lengths = value.split(" ").map(Number.parseFloat);
    const top = lengths[0] ?? 0;
    const right = lengths[1] ?? top;
    const bottom = lengths[2] ?? top;
    const left = lengths[3] ?? right;

    return [top, right, bottom, left];
  }

  function renderOutlookButton(option: UnlayerPollOptionValue, values: UnlayerPollValues): string {
    const padding = normalizeCssLengths(values.buttonPadding, "12px");
    const [paddingTop, , paddingBottom] = expandCssLengths(padding);
    const height = 18 + paddingTop + paddingBottom;
    const radius = Number.parseFloat(normalizeCssLengths(values.buttonBorderRadius, "6px"));
    const arcSize = Number(Math.min(100, (radius / height) * 100).toFixed(2));
    const backgroundColor = normalizeColor(values.buttonColor, "#185abc");
    const textColor = normalizeColor(values.buttonTextColor, "#ffffff");
    const href = `${pollResponseUrlPrefix}${option.id}`;

    return [
      '<td style="padding:4px;">',
      `<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${escapeHtml(href)}" style="height:${height}px;v-text-anchor:middle;width:120px;" arcsize="${arcSize}%" stroke="f" fillcolor="${backgroundColor}">`,
      "<w:anchorlock/>",
      `<center style="color:${textColor};font-family:Arial, sans-serif;font-size:14px;font-weight:700;line-height:18px;">`,
      escapeHtml(option.text),
      "</center>",
      "</v:roundrect>",
      "</td>",
    ].join("");
  }

  function renderOutlookButtons(
    options: UnlayerPollOptionValue[],
    values: UnlayerPollValues,
    align: "center" | "left" | "right",
  ): string {
    const rows: string[] = [];

    for (let index = 0; index < options.length; index += 3) {
      const cells = options
        .slice(index, index + 3)
        .map((option) => renderOutlookButton(option, values))
        .join("");
      rows.push(
        `<table role="presentation" align="${align}" border="0" cellpadding="0" cellspacing="0"><tr>${cells}</tr></table>`,
      );
    }

    return rows.join("");
  }

  function renderPoll(values: UnlayerPollValues): string {
    const options = normalizePollOptions(values).filter(
      (option) => option.id.trim().length > 0 && option.text.trim().length > 0,
    );
    const align = normalizeAlignment(values.alignment);
    const buttons = options.map((option) => renderButton(option, values)).join("");
    const question =
      typeof values.question === "string" && values.question.trim().length > 0
        ? values.question
        : "Enter your poll question";
    const questionColor = normalizeColor(values.questionColor, "#111827");

    return [
      "<!--[if mso]>",
      '<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr>',
      `<td align="${align}" style="padding:20px;font-family:Arial, sans-serif;">`,
      `<table role="presentation" align="${align}" border="0" cellpadding="0" cellspacing="0"><tr>`,
      `<td style="padding:0 0 12px;color:${questionColor};font-family:Arial, sans-serif;font-size:18px;font-weight:700;line-height:24px;">`,
      escapeHtml(question),
      "</td></tr></table>",
      renderOutlookButtons(options, values, align),
      "</td></tr></table>",
      "<![endif]-->",
      "<!--[if !mso]><!-->",
      `<div style="padding:20px;text-align:${align};font-family:Arial, sans-serif;">`,
      `<div style="margin-bottom:12px;color:${questionColor};font-size:18px;font-weight:700;line-height:24px;">`,
      escapeHtml(question),
      "</div>",
      `<div style="text-align:${align};">`,
      buttons,
      "</div>",
      "</div>",
      "<!--<![endif]-->",
    ].join("");
  }

  function validatePoll(data: { defaultErrors?: unknown; values?: UnlayerPollValues }): string[] {
    const errors = Array.isArray(data.defaultErrors)
      ? data.defaultErrors.filter((error): error is string => typeof error === "string")
      : [];
    const values = data.values || {};
    const question = typeof values.question === "string" ? values.question.trim() : "";
    const options = normalizePollOptions(values).filter(
      (option) => option.id.trim().length > 0 && option.text.trim().length > 0,
    );

    if (!question || question === "Enter your poll question") {
      errors.push("Enter a poll question.");
    }
    if (options.length === 0) {
      errors.push("Add at least one poll choice.");
    }

    return errors;
  }

  registerPollChoicesEditor();

  const renderer: {
    Viewer?: unknown;
    exporters: {
      email: (values: UnlayerPollValues) => string;
      web: (values: UnlayerPollValues) => string;
    };
    head: { css: () => string; js: () => string };
  } = {
    exporters: {
      email: renderPoll,
      web: renderPoll,
    },
    head: {
      css: () => "",
      js: () => "",
    },
  };

  if (typeof unlayerHost.createViewer === "function") {
    renderer.Viewer = unlayerHost.createViewer({
      render: renderPoll,
    });
  }

  unlayerHost.registerTool({
    ...toolDefinition,
    renderer,
    validator: validatePoll,
  });
}
